import {
	BackgroundAttributes,
	getBackgroundCSS,
	getSpacingCSS,
	getTypographyCSS,
} from '@redfox-thank-you/control-groups';
import { createAttributeManager } from '@redfox-thank-you/utils';
import { applyFilters } from '@wordpress/hooks';

import { getTableColorsCSS } from './components/table-colors/style';
import type { OrderDetailsAttributes, TableColorsAttributes } from './types';

/**
 * Generate CSS for the Order Details block.
 */
export const generateOrderDetailsCSS = (
	attributes: OrderDetailsAttributes,
	blockClass: string,
): string => {
	const css: string[] = [];
	const blockSelector = `.${ blockClass }`;

	// Create attribute manager to safely access nested attributes
	const { getAttribute } = createAttributeManager(
		attributes as Record< string, unknown >,
		() => {},
	);

	// Extract style properties with defaults using getAttribute
	const blockTextStyle = getAttribute( 'style.block.text', {} );
	const blockBackgroundStyle = getAttribute(
		'style.block.background',
		{},
	) as BackgroundAttributes;
	const blockSpacingStyle = getAttribute( 'style.block.spacing', {} );
	const titleStyle = getAttribute( 'style.title', {} );
	const tableColors = getAttribute< TableColorsAttributes >(
		'style.table',
		{},
	);

	// Extract settings properties with defaults using getAttribute
	const showTitle = getAttribute< boolean >( 'settings.title.show', true );
	const productImageSize = getAttribute< number >(
		'settings.layout.productImageSize',
		50,
	);

	// Title styles
	if ( showTitle ) {
		const titleSelector = `${ blockSelector } .redfox-thank-you-order-details__title`;
		const titleTypographyCSS = getTypographyCSS(
			titleStyle,
			titleSelector,
		);
		if ( titleTypographyCSS ) {
			css.push( titleTypographyCSS );
		}
	}

	// Block text styles
	const blockTextTypographyCSS = getTypographyCSS(
		blockTextStyle,
		blockSelector,
	);
	if ( blockTextTypographyCSS ) {
		css.push( blockTextTypographyCSS );
	}

	// Background styles - responsive
	const backgroundCSS = getBackgroundCSS(
		blockBackgroundStyle,
		blockSelector,
	);
	if ( backgroundCSS ) {
		css.push( backgroundCSS );
	}

	// Spacing styles - Generate for desktop, tablet, and mobile
	const spacingStyles = getSpacingCSS( blockSpacingStyle, blockSelector );
	if ( spacingStyles ) {
		css.push( spacingStyles );
	}

	// Table colors styles - responsive
	const tableColorsCSS = getTableColorsCSS( tableColors, blockSelector );
	if ( tableColorsCSS ) {
		css.push( tableColorsCSS );
	}

	// Product image size styles
	if ( productImageSize && productImageSize !== 50 ) {
		css.push(
			`${ blockSelector } .redfox-thank-you-order-details__product-image { width: ${ productImageSize }px; height: ${ productImageSize }px; }`,
		);
	}

	// Add overlay support styles - responsive aware
	const hasOverlay = [ 'desktop', 'tablet', 'mobile' ].some(
		( breakpoint ) => {
			const breakpointKey =
				breakpoint as keyof typeof blockBackgroundStyle;
			const breakpointValues = blockBackgroundStyle[ breakpointKey ];
			return breakpointValues?.backgroundOverlay?.enabled;
		},
	);

	if ( hasOverlay ) {
		css.push( `
			${ blockSelector } {
				position: relative;
			}
			${ blockSelector } .redfox-thank-you-order-details__title,
			${ blockSelector } .redfox-thank-you-order-details__content {
				position: relative;
				z-index: 1;
			}
		` );
	}

	/**
	 * Filter to extend block CSS generation.
	 * Pro plugins can use this to add additional CSS rules (e.g., border, box-shadow).
	 *
	 * @param {string} css - Current CSS string
	 * @param {Object} props - Props containing attributes and selector
	 * @param {Object} props.attributes - Block attributes
	 * @param {string} props.blockSelector - CSS selector for the block
	 * @param {string} props.blockName - Block name identifier
	 */
	const extendedCSS = applyFilters(
		'redfox_thank_you.block.css',
		css.filter( Boolean ).join( '\n' ),
		{
			attributes,
			blockSelector,
			blockName: 'redfox-thank-you/order-details',
		},
	) as string;

	return extendedCSS;
};
